/**
 * @file analytics.tsx
 *
 * @fileoverview Logs events into google analytics by default. Specific analytics implementations
 * should copy this file into src/overrides/services/analalytics and provide their own implementation there.
 */
import { Event, EVENT_TYPES, EventParameters } from '~/jobiqo/lib/analytics';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { getFrontend } from '../utils/getFrontend';
import {
  callMockEventSlot,
  logAnalyticsEventSlot,
  pollLogAnalyticsEventSlot,
  pollLogAnalyticsEventSlotImplemented,
} from '~/jobiqo/components/Slots/logAnalyticsEventSlot';

const getAnalyticsFrontendConfig = () => getCurrentFrontendConfig();

/**
 * Gets the API path with base path prefix.
 *
 * @param path
 *  The API path.
 * @returns
 *  The API path with base path prefix if configured.
 */
function getApiPath(path: string): string {
  const basePath = getAnalyticsFrontendConfig().APP.BASE_PATH || '';
  return `${basePath}${path}`;
}

/**
 * Calls the mock event.
 *
 * @param body
 *  The body of the event.
 */
function callMockEvent(body: { event: string; params: any }) {
  // Mock normal events for testing.
  fetch(getApiPath('/ga4-mock/event'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  // Mock server side events also for testing.
  fetch(getApiPath('/ga4-mock/event'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      event: `ss_${body.event}`,
      params: getServerSideParams(
        body.params,
        body.params.value,
        `ss_${body.event}`,
      ),
    }),
  });

  // Mock the client's custom events/dimensions.
  callMockEventSlot(body);
}

/**
 * Removes user sensitive information from the params.
 *
 * @param params
 *  The params to remove the user sensitive information from.
 * @param value
 *  The value for the event.
 * @returns
 *  The params without the user sensitive information.
 */
function getServerSideParams(params, value, eventName) {
  const entityIdEventNamesList = [
    'ss_job_visit',
    'ss_job_edit',
    'ss_job_bookmark',
    'ss_job_search',
    'ss_view_original_ad_btn_click',
    'ss_job_apply_start',
    'ss_job_apply_complete',
    'ss_job_alert_prompt',
    'ss_orgprofile_visit',
    'ss_minisite_landing_page_visit',
  ];

  return {
    ...Object.keys(params)
      .filter((key) => {
        // Exclude keys that contain user, session, owner, referrer
        if (
          key.includes('user') ||
          key.includes('session') ||
          key.includes('owner') ||
          key.includes('referrer')
        ) {
          return false;
        }

        // Only include entity_id if eventName is in the list
        if (
          key === 'entity_id' &&
          !entityIdEventNamesList.includes(eventName)
        ) {
          return false;
        }

        return true;
      })
      .reduce((obj, key) => {
        obj[key] = params[key];
        return obj;
      }, {}),
    ...{
      value,
      server: 1,
      current_frontend: getFrontend(getAnalyticsFrontendConfig()),
    },
  };
}

/**
 * Allows for an analytics dispatch for a certain event.
 *
 * @param event
 *   The event to dispatch.
 * @param params
 *   The event parameters.
 * @param value
 *   The value for the event.
 */
export const logAnalyticsEvent = (
  event: Event,
  params: EventParameters = {},
  value = 1,
  skipSST = false,
) => {
  const analyticsConfig = getAnalyticsFrontendConfig().APP;
  switch (event.type) {
    case EVENT_TYPES.CLIENT:
      if (typeof window !== 'undefined' && (window as any)?.gtag) {
        // Client side event.
        (window as any).gtag('event', event.key, { ...params, value });
      }

      if (analyticsConfig.GOOGLE_MEASUREMENT_API_ENABLED && !skipSST) {
        logAnalyticsServerEvent(event, params, value);
      }

      if (
        analyticsConfig.CONVERSION_API_ENABLED &&
        analyticsConfig.CONVERSION_API_ENABLED_EVENTS.includes(event.key)
      ) {
        logConversionApiEvent(event, params);
      }

      // When we run tests we want to do some testing on the client side.
      if (process.env.testing) {
        callMockEvent({
          event: event.key,
          params: { ...params, value },
        });
      }

      break;
    case EVENT_TYPES.SERVER:
      break;
    case EVENT_TYPES.HYBRID:
      break;
    default:
      break;
  }

  // Log any custom analytics events.
  logAnalyticsEventSlot(event, params, value);
};

/**
 * Logs an analytics event on the server side.
 *
 * @param event
 *  The event to dispatch.
 * @param params
 *  The event parameters.
 * @param value
 *  The value for the event.
 *
 * @returns void
 */
export function logAnalyticsServerEvent(
  event: Event,
  params: EventParameters = {},
  value = 1,
) {
  // Server side event.
  fetch(getApiPath('/api/gmapi'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      event: `ss_${event.key}`,
      params: getServerSideParams(params, value, `ss_${event.key}`),
    }),
  });
}

/**
 *  Logs a conversion API event.
 *
 * @param event
 *  The event to dispatch.
 * @param params
 * The event parameters.
 */
export function logConversionApiEvent(
  event: Event,
  params: EventParameters = {},
) {
  // Server side event.
  fetch(getApiPath('/api/conversionapi'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      event_name: event.key,
      custom_data: params,
    }),
  });
}

/**
 * Allows for polling an analytics dispatch for a certain amount of time.
 *
 * @param event
 *   The event to dispatch.
 * @param params
 *   The event parameters.
 * @param value
 *   The value for the event.
 * @param time
 *   The time for the polling.
 */
export const pollLogAnalyticsEvent = (
  event: Event,
  params: EventParameters = {},
  value: number = 1,
  time: number = 500,
  counter: number = 1,
  maximumTries: number = 30,
) => {
  // When we run tests we want to do some testing on the client side.
  if (process.env.testing) {
    callMockEvent({
      event: event.key,
      params: { ...params, value },
    });

    // Exit early. In testing we want to only mock the event.
    return;
  }

  // If the slot is implemented we call it,
  // otherwise we call the default implementation.
  if (pollLogAnalyticsEventSlotImplemented) {
    pollLogAnalyticsEventSlot(
      event,
      params,
      value,
      time,
      counter,
      maximumTries,
    );
  } else {
    let tries = counter;
    if (counter <= maximumTries) {
      setTimeout(() => {
        if (!(window as any).gtag || !(window as any).gtag_fully_loaded) {
          tries++;
          pollLogAnalyticsEvent(
            event,
            params,
            value,
            time,
            tries,
            maximumTries,
          );
        } else {
          logAnalyticsEvent(event, params, value, true);
        }
      }, time);
    }
  }
};
